import type { CodeableConcept, Extension } from '@medplum/fhirtypes';
import { SCRIPTSURE_FORMULATION_EXTENSION } from './identifiers';
import { getMedicationRxNormCode, type MedicationListRecord } from './medicationRecords';

/**
 * The `$drug-search` formulation row a prescriber picked, persisted on `medicationCodeableConcept`
 * as one complex {@link SCRIPTSURE_FORMULATION_EXTENSION} extension.
 *
 * Kept as the two pieces the picker holds rather than the string it composes from them: the title
 * renders the drug's name over its strength/form, and recovering that split from a composed string
 * is the parsing this field exists to retire. `drugName` is absent for a row from the RxNorm-catalog
 * fallback, whose `formulation` is already a complete product name.
 *
 * Only the picked row is recorded — never the other rows the search offered, which are a catalog
 * snapshot rather than patient data.
 */
export type ScriptSureFormulation = {
  /** The drug-level search row's name ("Metformin", "dextroamphetamine-amphetamine"). */
  drugName?: string;
  /** The formulation row's label as the vendor wrote it ("ER 500 mg tablet,extended release 24hr"). */
  formulation: string;
  /** Where the row came from: ScriptSure's `$drug-search`, or the RxNorm-catalog fallback. */
  catalog: 'scriptsure' | 'rxnorm';
  /** The RxNorm code the row carried — the staleness marker, as on `medication-names`: a coding
   * swapped since the pick (a drug changed at signing) makes the recorded formulation describe the
   * wrong product, and a consumer must treat it as absent rather than show it. */
  rxNorm?: string;
  /** The vendor's DEA schedule for the row ("0" for unscheduled, "2" for C-II). */
  deaSchedule?: string;
  /** The sig template the prescriber chose from the row, if one. */
  sig?: string;
  /** The vendor's saved-format key for that sig, which its text alone cannot recover. */
  sigFormatId?: number;
};

const SUB_URL = {
  drugName: 'drugName',
  formulation: 'formulation',
  catalog: 'catalog',
  rxnorm: 'rxnorm',
  deaSchedule: 'deaSchedule',
  sig: 'sig',
  sigFormatId: 'sigFormatId',
} as const;

/** Build the complex scriptsure-formulation extension. */
export function buildScriptSureFormulationExtension(formulation: ScriptSureFormulation): Extension {
  const parts: Extension[] = [];
  if (formulation.drugName) parts.push({ url: SUB_URL.drugName, valueString: formulation.drugName });
  parts.push({ url: SUB_URL.formulation, valueString: formulation.formulation });
  parts.push({ url: SUB_URL.catalog, valueCode: formulation.catalog });
  if (formulation.rxNorm) parts.push({ url: SUB_URL.rxnorm, valueCode: formulation.rxNorm });
  if (formulation.deaSchedule) parts.push({ url: SUB_URL.deaSchedule, valueCode: formulation.deaSchedule });
  if (formulation.sig) parts.push({ url: SUB_URL.sig, valueString: formulation.sig });
  if (formulation.sigFormatId !== undefined)
    parts.push({ url: SUB_URL.sigFormatId, valueInteger: formulation.sigFormatId });
  return { url: SCRIPTSURE_FORMULATION_EXTENSION, extension: parts };
}

/** Read the picked formulation off a `medicationCodeableConcept`, or `undefined` when none was
 * recorded (records written before the field existed, manual and chat-created entries, imports) or
 * the recorded one is malformed. */
export function getScriptSureFormulation(concept: CodeableConcept | undefined): ScriptSureFormulation | undefined {
  const ext = concept?.extension?.find((e) => e.url === SCRIPTSURE_FORMULATION_EXTENSION);
  if (!ext) return undefined;
  const part = (url: string): Extension | undefined => ext.extension?.find((e) => e.url === url);

  const formulation = part(SUB_URL.formulation)?.valueString;
  const catalog = part(SUB_URL.catalog)?.valueCode;
  if (!formulation || (catalog !== 'scriptsure' && catalog !== 'rxnorm')) return undefined;

  const sigFormatId = part(SUB_URL.sigFormatId)?.valueInteger;
  return {
    drugName: part(SUB_URL.drugName)?.valueString,
    formulation,
    catalog,
    rxNorm: part(SUB_URL.rxnorm)?.valueCode,
    deaSchedule: part(SUB_URL.deaSchedule)?.valueCode,
    sig: part(SUB_URL.sig)?.valueString,
    ...(sigFormatId !== undefined ? { sigFormatId } : {}),
  };
}

/** The picked formulation, guarded by the staleness marker: a formulation recorded against a
 * different RxNorm code than the record now carries describes the wrong product, so it is treated
 * as absent. A formulation recorded without a code (an NDC-only device or supply) is never stale. */
export function getFreshScriptSureFormulation(
  record: MedicationListRecord | undefined,
): ScriptSureFormulation | undefined {
  if (!record) return undefined;
  const formulation = getScriptSureFormulation(record.medicationCodeableConcept);
  if (!formulation) return undefined;
  if (formulation.rxNorm && formulation.rxNorm !== getMedicationRxNormCode(record)) return undefined;
  return formulation;
}

/**
 * Compose a formulation's product name from the drug and the formulation label. ScriptSure's labels
 * are sometimes only the strength/form ("5 % (700 mg/patch) adhesive patch" under "lidocaine") and
 * sometimes the full product name ("metformin 500 MG Oral Tablet") — the drug name is prefixed only
 * when the label doesn't already carry it, so neither shape doubles up or loses the drug.
 *
 * The full drug-row name is used deliberately, digits and all: verified against the live catalog,
 * ScriptSure drug rows are plain brand/generic names ("lidocaine", "Salonpas (lidocaine)") whose
 * digits — when present — are product identity, not strength ("Loestrin 1.5/30 (21)", "Loestrin Fe
 * 1/20 (28-Day)"). Stripping at the first digit would collapse those distinct products.
 */
export function formulationDisplay(drugName: string | undefined, formulationName: string): string {
  if (!drugName || formulationName.toLowerCase().includes(drugName.toLowerCase())) return formulationName;
  return `${drugName} ${formulationName}`;
}
